/**
 * Influencer Management Page
 * 
 * Full CRUD interface for managing influencers.
 */

import React, { useEffect, useState, useRef } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  ExternalLink,
  Copy,
  Check,
  BarChart3,
} from 'lucide-react';
import { 
  AdminLayout, 
  DataTable, 
  Modal, 
  StatsCard,
  Input, 
  Textarea, 
  Toggle, 
  ColorPicker,
  type Column,
  type Action,
} from '../../components/admin';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Influencer {
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  bio?: string;
  profileImage?: string;
  bannerImage?: string;
  socialLinks?: {
    twitter?: string;
    youtube?: string;
    instagram?: string;
    website?: string;
  };
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
  };
  testimonial?: string;
  specialOffer?: {
    title?: string;
    description?: string;
    discountCode?: string;
    discountPercent?: number;
  };
  stats?: {
    followers?: string;
    experience?: string;
    trades?: string;
  };
  isActive: boolean;
  createdAt: string;
  totalClicks: number;
  uniqueVisitors: number;
  totalSignups: number;
}

const defaultInfluencer: Partial<Influencer> = {
  name: '',
  slug: '',
  tagline: '',
  bio: '',
  profileImage: '',
  isActive: true,
  branding: {
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    accentColor: '#10b981',
  },
  socialLinks: {},
  specialOffer: {
    discountPercent: 0,
  },
  stats: {},
};

const InfluencerManagement: React.FC = () => {
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [formData, setFormData] = useState<Partial<Influencer>>(defaultInfluencer);
  const [saving, setSaving] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const bannerImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/influencer/admin/list?includeInactive=true`);
      const result = await response.json();
      if (result.success) {
        setInfluencers(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedInfluencer(null);
    setFormData(defaultInfluencer);
    setModalOpen(true);
  };

  const handleEdit = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setFormData(influencer);
    setModalOpen(true);
  };

  const handleDelete = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setDeleteModalOpen(true);
  };

  const handleCopyLink = (slug: string) => {
    const url = `${window.location.origin}/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const handleCopyInfluencer = (influencer: Influencer) => {
    // Create a copy of the influencer with modified name and slug
    const copiedData: Partial<Influencer> = {
      ...influencer,
      name: `${influencer.name} (Copy)`,
      slug: `${influencer.slug}-copy`,
      isActive: false, // Start as inactive to avoid conflicts
      // Reset stats for the copy
      totalClicks: 0,
      totalSignups: 0,
    };
    // Remove the id so it creates a new record
    delete (copiedData as any).id;
    delete (copiedData as any).createdAt;
    
    setSelectedInfluencer(null); // This is a new record, not editing
    setFormData(copiedData);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = selectedInfluencer 
        ? `${API_BASE}/api/influencer/admin/${selectedInfluencer.id}`
        : `${API_BASE}/api/influencer/admin/create`;
      
      const response = await fetch(url, {
        method: selectedInfluencer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setModalOpen(false);
        fetchInfluencers();
      } else {
        alert(result.error || 'Failed to save influencer');
      }
    } catch (error) {
      console.error('Failed to save influencer:', error);
      alert('Failed to save influencer');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedInfluencer) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/influencer/admin/${selectedInfluencer.id}`, {
        method: 'DELETE',
      });

      const result = await response.json();
      
      if (result.success) {
        setDeleteModalOpen(false);
        fetchInfluencers();
      } else {
        alert(result.error || 'Failed to delete influencer');
      }
    } catch (error) {
      console.error('Failed to delete influencer:', error);
      alert('Failed to delete influencer');
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      }
      // Handle nested fields like 'branding.primaryColor'
      const [parent, child] = keys;
      return {
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value,
        },
      };
    });
  };

  const handleImageUpload = async (file: File, field: 'profileImage' | 'bannerImage') => {
    try {
      // SECURITY: Validate file type using MIME type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPG, PNG, GIF, WebP, SVG, BMP, TIFF)');
        return;
      }

      // SECURITY: Validate file extension to prevent MIME type spoofing
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.tif'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!validExtensions.includes(fileExtension)) {
        alert('Invalid file extension. Please use: JPG, PNG, GIF, WebP, SVG, BMP, or TIFF');
        return;
      }

      // SECURITY: Validate file size (max 5MB to prevent DoS)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }

      // SECURITY: Additional validation for SVG files to prevent XSS
      if (file.type === 'image/svg+xml') {
        const svgContent = await file.text();
        
        // Check for dangerous patterns in SVG
        const dangerousPatterns = [
          /<script/i,
          /javascript:/i,
          /on\w+\s*=/i, // onclick, onload, etc.
          /<iframe/i,
          /<embed/i,
          /<object/i,
          /xlink:href\s*=\s*["']javascript:/i,
        ];
        
        const hasDangerousContent = dangerousPatterns.some(pattern => pattern.test(svgContent));
        if (hasDangerousContent) {
          alert('SVG file contains potentially dangerous content and cannot be uploaded');
          console.warn('Blocked SVG upload with dangerous content:', file.name);
          return;
        }

        // SECURITY: Sanitize SVG by removing dangerous attributes
        const sanitizedSvg = svgContent
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
          .replace(/javascript:/gi, '')
          .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
          .replace(/<embed[^>]*>/gi, '')
          .replace(/<object[^>]*>.*?<\/object>/gi, '');
        
        // Convert sanitized SVG to data URL
        const blob = new Blob([sanitizedSvg], { type: 'image/svg+xml' });
        const sanitizedFile = new File([blob], file.name, { type: 'image/svg+xml' });
        
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          updateFormData(field, dataUrl);
        };
        reader.onerror = () => {
          alert('Failed to read image file');
        };
        reader.readAsDataURL(sanitizedFile);
        return;
      }

      // SECURITY: Verify magic numbers for non-SVG images to prevent file type spoofing
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      const magicNumbers: { [key: string]: number[][] } = {
        'image/jpeg': [[0xFF, 0xD8, 0xFF]],
        'image/png': [[0x89, 0x50, 0x4E, 0x47]],
        'image/gif': [[0x47, 0x49, 0x46, 0x38]],
        'image/webp': [[0x52, 0x49, 0x46, 0x46]], // RIFF header
        'image/bmp': [[0x42, 0x4D]],
        'image/tiff': [[0x49, 0x49, 0x2A, 0x00], [0x4D, 0x4D, 0x00, 0x2A]],
      };

      const expectedMagicNumbers = magicNumbers[file.type];
      if (expectedMagicNumbers) {
        const isValid = expectedMagicNumbers.some(magic => 
          magic.every((byte, index) => uint8Array[index] === byte)
        );
        
        if (!isValid) {
          alert('File content does not match the declared image type. Upload rejected.');
          console.warn('Magic number validation failed for:', file.name, file.type);
          return;
        }
      }

      // Convert to base64 data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // SECURITY: Validate data URL format
        if (!dataUrl || !dataUrl.startsWith('data:image/')) {
          alert('Invalid image data generated');
          return;
        }
        updateFormData(field, dataUrl);
      };
      reader.onerror = () => {
        alert('Failed to read image file');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    }
  };

  const triggerImageUpload = (type: 'profile' | 'banner') => {
    if (type === 'profile') {
      profileImageInputRef.current?.click();
    } else {
      bannerImageInputRef.current?.click();
    }
  };

  const columns: Column<Influencer>[] = [
    {
      key: 'name',
      header: 'Influencer',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-3">
          {item.profileImage ? (
            <img 
              src={item.profileImage} 
              alt={item.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
              style={{ backgroundColor: item.branding?.primaryColor || '#3b82f6' }}
            >
              {item.name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-medium text-white">{item.name}</p>
            <p className="text-xs text-slate-500">/{item.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (item) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          item.isActive 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-slate-500/20 text-slate-400'
        }`}>
          {item.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'totalClicks',
      header: 'Clicks',
      sortable: true,
      className: 'text-right',
      render: (item) => (
        <span className="text-slate-300">{item.totalClicks.toLocaleString()}</span>
      ),
    },
    {
      key: 'uniqueVisitors',
      header: 'Unique',
      sortable: true,
      className: 'text-right',
      render: (item) => (
        <span className="text-slate-300">{(item.uniqueVisitors || 0).toLocaleString()}</span>
      ),
    },
    {
      key: 'totalSignups',
      header: 'Signups',
      sortable: true,
      className: 'text-right',
      render: (item) => (
        <span className="text-slate-300">{item.totalSignups.toLocaleString()}</span>
      ),
    },
    {
      key: 'conversionRate',
      header: 'Conv. Rate',
      className: 'text-right',
      render: (item) => {
        const uniqueVisitors = item.uniqueVisitors || 0;
        const rate = uniqueVisitors > 0 
          ? ((item.totalSignups / uniqueVisitors) * 100).toFixed(1)
          : '0';
        return (
          <span className={`font-medium ${
            parseFloat(rate) >= 5 ? 'text-green-400' :
            parseFloat(rate) >= 2 ? 'text-yellow-400' : 'text-slate-400'
          }`}>
            {rate}%
          </span>
        );
      },
    },
    {
      key: 'link',
      header: 'Link',
      render: (item) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCopyLink(item.slug)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            title="Copy link"
          >
            {copiedSlug === item.slug ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
          <a
            href={`/${item.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            title="Preview"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      ),
    },
  ];

  const actions: Action<Influencer>[] = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: handleEdit,
    },
    {
      label: 'Copy',
      icon: Copy,
      onClick: handleCopyInfluencer,
    },
    {
      label: 'View Stats',
      icon: BarChart3,
      onClick: (item) => {
        const uniqueVisitors = item.uniqueVisitors || 0;
        alert(`Total Clicks: ${item.totalClicks}\nUnique Visitors: ${uniqueVisitors}\nSignups: ${item.totalSignups}\nConversion: ${
          uniqueVisitors > 0 ? ((item.totalSignups / uniqueVisitors) * 100).toFixed(2) : 0
        }%`);
      },
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: handleDelete,
      variant: 'danger',
    },
  ];

  const totalClicks = influencers.reduce((sum, inf) => sum + inf.totalClicks, 0);
  const totalUniqueVisitors = influencers.reduce((sum, inf) => sum + (inf.uniqueVisitors || 0), 0);
  const totalSignups = influencers.reduce((sum, inf) => sum + inf.totalSignups, 0);
  const activeCount = influencers.filter(inf => inf.isActive).length;

  return (
    <AdminLayout
      title="Influencer Management"
      subtitle="Manage your influencer landing pages and track performance"
      actions={
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Influencer
        </button>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Active Influencers"
          value={activeCount}
          icon={Eye}
          color="green"
        />
        <StatsCard
          title="Total Clicks"
          value={totalClicks.toLocaleString()}
          icon={Eye}
          color="blue"
        />
        <StatsCard
          title="Unique Visitors"
          value={totalUniqueVisitors.toLocaleString()}
          icon={Eye}
          color="orange"
        />
        <StatsCard
          title="Total Signups"
          value={totalSignups.toLocaleString()}
          icon={Eye}
          color="purple"
        />
      </div>

      {/* Table */}
      <DataTable
        data={influencers}
        columns={columns}
        actions={actions}
        keyField="id"
        loading={loading}
        searchPlaceholder="Search influencers..."
        emptyMessage="No influencers found. Click 'Add Influencer' to create one."
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedInfluencer ? 'Edit Influencer' : 'Add New Influencer'}
        size="lg"
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Name"
                value={formData.name || ''}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="Trading Guru"
                required
              />
              <Input
                label="Slug (URL)"
                value={formData.slug || ''}
                onChange={(e) => updateFormData('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="tradingguru"
                hint="Used in URL: /tradingguru"
                required
              />
            </div>
            <Input
              label="Tagline"
              value={formData.tagline || ''}
              onChange={(e) => updateFormData('tagline', e.target.value)}
              placeholder="Master the markets with AI-powered analysis"
            />
            <Textarea
              label="Bio"
              value={formData.bio || ''}
              onChange={(e) => updateFormData('bio', e.target.value)}
              placeholder="Short bio about the influencer..."
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Profile Image</label>
              <div className="flex gap-3 items-start">
                <div className="flex-1">
                  <Input
                    label=""
                    value={formData.profileImage || ''}
                    onChange={(e) => updateFormData('profileImage', e.target.value)}
                    placeholder="https://example.com/image.jpg or upload below"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => triggerImageUpload('profile')}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Upload Image
                </button>
              </div>
              <input
                ref={profileImageInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp,image/tiff"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file, 'profileImage');
                }}
                className="hidden"
              />
              {formData.profileImage && (
                <div className="mt-2">
                  <img
                    src={formData.profileImage}
                    alt="Profile preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-slate-600"
                  />
                </div>
              )}
              <p className="text-xs text-slate-500">Supports: JPG, PNG, GIF, WebP, SVG, BMP, TIFF (max 5MB)</p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Banner Image</label>
              <div className="flex gap-3 items-start">
                <div className="flex-1">
                  <Input
                    label=""
                    value={formData.bannerImage || ''}
                    onChange={(e) => updateFormData('bannerImage', e.target.value)}
                    placeholder="https://example.com/banner.jpg or upload below"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => triggerImageUpload('banner')}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Upload Image
                </button>
              </div>
              <input
                ref={bannerImageInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp,image/tiff"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file, 'bannerImage');
                }}
                className="hidden"
              />
              {formData.bannerImage && (
                <div className="mt-2">
                  <img
                    src={formData.bannerImage}
                    alt="Banner preview"
                    className="w-full h-32 rounded-lg object-cover border-2 border-slate-600"
                  />
                </div>
              )}
              <p className="text-xs text-slate-500">Supports: JPG, PNG, GIF, WebP, SVG, BMP, TIFF (max 5MB)</p>
            </div>
            <Textarea
              label="Testimonial Quote"
              value={formData.testimonial || ''}
              onChange={(e) => updateFormData('testimonial', e.target.value)}
              placeholder="What the influencer says about TradeRewind..."
            />
          </div>

          {/* Branding */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Branding Colors</h4>
            <div className="grid grid-cols-3 gap-4">
              <ColorPicker
                label="Primary"
                value={formData.branding?.primaryColor || '#3b82f6'}
                onChange={(color) => updateFormData('branding.primaryColor', color)}
              />
              <ColorPicker
                label="Secondary"
                value={formData.branding?.secondaryColor || '#8b5cf6'}
                onChange={(color) => updateFormData('branding.secondaryColor', color)}
              />
              <ColorPicker
                label="Accent"
                value={formData.branding?.accentColor || '#10b981'}
                onChange={(color) => updateFormData('branding.accentColor', color)}
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Social Links</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Twitter"
                value={formData.socialLinks?.twitter || ''}
                onChange={(e) => updateFormData('socialLinks.twitter', e.target.value)}
                placeholder="https://twitter.com/handle"
              />
              <Input
                label="YouTube"
                value={formData.socialLinks?.youtube || ''}
                onChange={(e) => updateFormData('socialLinks.youtube', e.target.value)}
                placeholder="https://youtube.com/@channel"
              />
              <Input
                label="Instagram"
                value={formData.socialLinks?.instagram || ''}
                onChange={(e) => updateFormData('socialLinks.instagram', e.target.value)}
                placeholder="https://instagram.com/handle"
              />
              <Input
                label="Website"
                value={formData.socialLinks?.website || ''}
                onChange={(e) => updateFormData('socialLinks.website', e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Special Offer */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Special Offer</h4>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Offer Title"
                value={formData.specialOffer?.title || ''}
                onChange={(e) => updateFormData('specialOffer.title', e.target.value)}
                placeholder="20% OFF First Month"
              />
              <Input
                label="Discount Code"
                value={formData.specialOffer?.discountCode || ''}
                onChange={(e) => updateFormData('specialOffer.discountCode', e.target.value.toUpperCase())}
                placeholder="GURU20"
              />
            </div>
            <Input
              label="Discount Percent"
              type="number"
              value={formData.specialOffer?.discountPercent || 0}
              onChange={(e) => updateFormData('specialOffer.discountPercent', parseInt(e.target.value) || 0)}
              min={0}
              max={100}
            />
            <Input
              label="Offer Description"
              value={formData.specialOffer?.description || ''}
              onChange={(e) => updateFormData('specialOffer.description', e.target.value)}
              placeholder="Use my exclusive code for 20% off!"
            />
          </div>

          {/* Stats Display */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Display Stats</h4>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Followers"
                value={formData.stats?.followers || ''}
                onChange={(e) => updateFormData('stats.followers', e.target.value)}
                placeholder="500K+"
              />
              <Input
                label="Experience"
                value={formData.stats?.experience || ''}
                onChange={(e) => updateFormData('stats.experience', e.target.value)}
                placeholder="10+ years"
              />
              <Input
                label="Trades"
                value={formData.stats?.trades || ''}
                onChange={(e) => updateFormData('stats.trades', e.target.value)}
                placeholder="50K+ trades"
              />
            </div>
          </div>

          {/* Status */}
          <div className="pt-4 border-t border-slate-800">
            <Toggle
              label="Active"
              description="When active, the landing page is publicly accessible"
              checked={formData.isActive ?? true}
              onChange={(checked) => updateFormData('isActive', checked)}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
          <button
            onClick={() => setModalOpen(false)}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !formData.name || !formData.slug}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors"
          >
            {saving ? 'Saving...' : selectedInfluencer ? 'Save Changes' : 'Create Influencer'}
          </button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Influencer"
        size="sm"
      >
        <p className="text-slate-300 mb-6">
          Are you sure you want to delete <strong className="text-white">{selectedInfluencer?.name}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setDeleteModalOpen(false)}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors"
          >
            Delete
          </button>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default InfluencerManagement;
